/**
 * Administration → Storage → Old versions (1.2.3, ?view=old-versions): find earlier versions of assets and move them
 * to the recycle bin, for people with Delete old versions and Manage storage. Scan with the criteria (keep the latest N
 * versions, older than D days, a class, a location), read what it found (per location what PRISM frees and what is in
 * place, the largest, what was skipped because something uses it), then Move to recycle bin after typing the number.
 * The job runs in the background in batches, checks each version again, and can be cancelled; the space comes back
 * when the bin purges them.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, CircleCheck, CircleX, Search, Square, Trash2 } from 'lucide-react';
import {
  VERSION_USE_LABELS, describeVersionUse, type OldVersionsCriteria, type OldVersionsScan, type StorageLocationSummary, type VersionCleanupJobDTO, type VersionUseKind,
} from '@prism/contracts';
import { errorMessage } from '../../../api/client';
import { useCategories, useOldVersions } from '../../../api/hooks';
import { ProgressLine } from '../../../components/JobProgress';
import { TableScroll } from '../../../components/TableScroll';
import { toast } from '../../../components/Toasts';
import { bytes, dateTime, relative } from '../../../lib/format';

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
/** "19 Nov": the day the bin purges them. */
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');
const LINK = { color: 'var(--accent-strong)' };
const REASONS = (r: Partial<Record<VersionUseKind | 'changed', number>>) =>
  Object.entries(r).filter(([, n]) => n).map(([k, n]) => `${VERSION_USE_LABELS[k as VersionUseKind] ?? k}: ${n}`).join(' · ');

export function OldVersions({ locations }: { locations: StorageLocationSummary[] }) {
  const categories = useCategories().data?.items ?? [];
  const { jobs, scan, cleanup, cancel } = useOldVersions();
  const [f, setF] = useState({ keep: '1', days: '30', categoryId: '', locationId: '' });
  const [result, setResult] = useState<OldVersionsScan | null>(null);
  const [typed, setTyped] = useState('');
  const criteria: OldVersionsCriteria = {
    keep: Math.min(10, Math.max(1, Math.round(Number(f.keep) || 1))), olderThanDays: Math.max(0, Math.round(Number(f.days) || 0)),
    categoryIds: f.categoryId ? [f.categoryId] : [], locationId: f.locationId || null,
  };
  const latest = jobs.data?.items[0] ?? null;
  const active = latest && (latest.status === 'queued' || latest.status === 'running') ? latest : null;
  const set = <K extends keyof typeof f>(k: K, v: string) => { setF((x) => ({ ...x, [k]: v })); setResult(null); setTyped(''); };

  const runScan = async () => {
    try {
      setResult(await scan.mutateAsync(criteria));
      setTyped('');
    } catch (err) { toast(errorMessage(err), 'error'); }
  };
  const start = async () => {
    if (!result) return;
    try {
      await cleanup.mutateAsync({ criteria: result.criteria, confirmCount: result.versions });
      toast(`Moving ${plural(result.versions, 'version')} to the recycle bin`);
      setResult(null);
      setTyped('');
    } catch (err) { toast(errorMessage(err), 'error'); }
  };

  return (
    <div data-testid="old-versions">
      <h2 style={{ margin: '0 0 4px' }}>Old versions</h2>
      <p className="lede">
        Earlier versions of assets keep their files until they are deleted. Find them here and move them to the recycle bin; the current version of an asset is never
        touched, nor is a version something still uses (a sequence, a kept export, an open hand-off, a pending approval, work in progress). The space comes back when the
        recycle bin purges them.
      </p>
      <section className="panel-card">
        <div className="old-versions-form">
          <div className="field" style={{ margin: 0 }}><label htmlFor="ov-keep">Keep the latest versions</label>
            <input id="ov-keep" className="input" type="number" min={1} max={10} value={f.keep} onChange={(e) => set('keep', e.target.value)} data-testid="old-versions-keep" />
            <span className="help">{criteria.keep === 1 ? 'Only the current version is kept.' : `The current version and ${criteria.keep - 1} before it.`}</span></div>
          <div className="field" style={{ margin: 0 }}><label htmlFor="ov-days">Older than (days)</label>
            <input id="ov-days" className="input" type="number" min={0} value={f.days} onChange={(e) => set('days', e.target.value)} data-testid="old-versions-days" />
            <span className="help">From when the version was made.</span></div>
          <div className="field" style={{ margin: 0 }}><label htmlFor="ov-class">Only assets of class</label>
            <select id="ov-class" className="select" value={f.categoryId} onChange={(e) => set('categoryId', e.target.value)} data-testid="old-versions-class">
              <option value="">Any class</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select><span className="help">&nbsp;</span></div>
          <div className="field" style={{ margin: 0 }}><label htmlFor="ov-location">With files on</label>
            <select id="ov-location" className="select" value={f.locationId} onChange={(e) => set('locationId', e.target.value)} data-testid="old-versions-location">
              <option value="">Any location</option>
              {locations.filter((l) => l.purpose === 'storage').map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select><span className="help">&nbsp;</span></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn primary" disabled={scan.isPending} onClick={() => void runScan()} data-testid="old-versions-scan"><Search size={14} /> {scan.isPending ? 'Scanning…' : 'Scan'}</button>
        </div>
      </section>

      {result && <ScanResult result={result} typed={typed} setTyped={setTyped} busy={cleanup.isPending || Boolean(active)} onStart={() => void start()} />}
      {latest && <JobCard job={latest} onCancel={() => cancel.mutate(latest.id, { onError: (e) => toast(errorMessage(e), 'error') })} cancelling={cancel.isPending} />}
    </div>
  );
}

function ScanResult({ result, typed, setTyped, busy, onStart }: { result: OldVersionsScan; typed: string; setTyped: (s: string) => void; busy: boolean; onStart: () => void }) {
  const r = result;
  const freed = r.byLocation.reduce((n, l) => n + l.managedBytes, 0);
  const inPlace = r.byLocation.reduce((n, l) => n + l.inPlaceBytes, 0);
  return (
    <section className="panel-card" style={{ marginTop: 16 }} data-testid="old-versions-result" data-versions={r.versions}>
      <p className="section-label">Found{r.partial ? ' (so far: the scan stopped at its time limit)' : ''}</p>
      <div className="old-versions-figures">
        <div><b data-testid="old-versions-count">{r.versions.toLocaleString()}</b><span>{r.versions === 1 ? 'version' : 'versions'} of {plural(r.assets, 'asset')}</span></div>
        <div><b>{bytes(r.bytes)}</b><span>in their files</span></div>
        <div><b>{bytes(freed)}</b><span>freed when purged</span></div>
        {inPlace > 0 && <div><b>{bytes(inPlace)}</b><span>in place, only uncatalogued</span></div>}
        {r.skipped.versions > 0 && <div><b>{r.skipped.versions.toLocaleString()}</b><span>skipped: in use</span></div>}
      </div>
      {r.versions > 0 && (
        <p className="muted" style={{ marginTop: 0 }} data-testid="old-versions-when">
          {r.retentionDays != null
            ? <>They stay in the recycle bin for {r.retentionDays} days: the space comes back on <span title={dateTime(r.purgeAfter)}>{day(r.purgeAfter)}</span> when the bin purges them, or <Link to="/recycle-bin" style={LINK}>empty the bin</Link> now.</>
            : <>The recycle bin is off: they can be restored for a few minutes, then their files are deleted.</>}
        </p>
      )}
      {r.byLocation.length > 0 && (
        <TableScroll>
          <table className="table compact" data-testid="old-versions-locations">
            <thead><tr><th>Location</th><th style={{ textAlign: 'right' }}>Managed by PRISM (freed)</th><th style={{ textAlign: 'right' }}>In place (kept on disk)</th><th style={{ textAlign: 'right' }}>Files</th></tr></thead>
            <tbody>{r.byLocation.map((l) => (
              <tr key={l.id}><td className="wrap">{l.name}</td><td className="nowrap" style={{ textAlign: 'right' }}>{bytes(l.managedBytes)}</td>
                <td className="nowrap" style={{ textAlign: 'right' }}>{l.inPlaceBytes ? bytes(l.inPlaceBytes) : '—'}</td><td className="nowrap" style={{ textAlign: 'right' }}>{l.files.toLocaleString()}</td></tr>
            ))}</tbody>
          </table>
        </TableScroll>
      )}
      {r.largest.length > 0 && (
        <details style={{ marginTop: 12 }} data-testid="old-versions-largest">
          <summary>The {Math.min(r.largest.length, 50)} largest</summary>
          <TableScroll>
            <table className="table compact" style={{ marginTop: 6 }}>
              <thead><tr><th>Asset</th><th>Version</th><th style={{ textAlign: 'right' }}>Size</th><th>Made</th></tr></thead>
              <tbody>{r.largest.map((x) => (
                <tr key={x.versionId}><td className="wrap"><Link to={`/assets/${x.assetId}?tab=versions`}>{x.title}</Link></td><td className="nowrap">{x.number}</td>
                  <td className="nowrap" style={{ textAlign: 'right' }}>{bytes(x.sizeBytes)}</td><td className="nowrap" title={dateTime(x.createdAt)}>{relative(x.createdAt)}</td></tr>
              ))}</tbody>
            </table>
          </TableScroll>
        </details>
      )}
      {r.skipped.versions > 0 && (
        <div style={{ marginTop: 12 }} data-testid="old-versions-skipped">
          <p className="section-label" style={{ marginBottom: 4 }}>Skipped: in use ({REASONS(r.skipped.byReason)})</p>
          <ul className="version-uses" style={{ color: 'var(--text-2)' }}>
            {r.skipped.sample.map((s, i) => (
              <li key={i}><span><Link to={`/assets/${s.assetId}?tab=versions`}>Version {s.number} of “{s.title}”</Link>: {describeVersionUse(s.use)}</span></li>
            ))}
          </ul>
        </div>
      )}
      {r.versions > 0 && (
        <div className="old-versions-confirm" style={{ marginTop: 16 }}>
          <label htmlFor="ov-confirm">Type <b>{r.versions}</b> to move {r.versions === 1 ? 'it' : 'them'} to the recycle bin</label>
          <input id="ov-confirm" className="input" inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="old-versions-confirm" />
          <button className="btn danger" disabled={busy || typed.trim() !== String(r.versions)} onClick={onStart} data-testid="old-versions-cleanup"><Trash2 size={14} /> Move to recycle bin</button>
          {busy && <span className="faint" style={{ fontSize: 12 }}>A job is running</span>}
        </div>
      )}
    </section>
  );
}

function JobCard({ job, onCancel, cancelling }: { job: VersionCleanupJobDTO; onCancel: () => void; cancelling: boolean }) {
  const running = job.status === 'queued' || job.status === 'running';
  const pct = job.maxVersions ? Math.min(100, (job.deleted / job.maxVersions) * 100) : null;
  return (
    <section className="panel-card" style={{ marginTop: 16 }} data-testid="old-versions-job" data-status={job.status}>
      <p className="section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Archive size={13} /> {running ? 'Moving to the recycle bin' : 'Last run'}
        <span className="faint" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>· started {relative(job.createdAt)}{job.requestedBy ? ` by ${job.requestedBy}` : ''}</span>
      </p>
      {running && <ProgressLine step={`${plural(job.deleted, 'version')} of at most ${job.maxVersions.toLocaleString()} moved · ${plural(job.processed, 'checked', 'checked')}`} percent={pct} />}
      {!running && (
        <p style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 6 }} data-testid="old-versions-job-result">
          {job.status === 'succeeded' ? <CircleCheck size={15} className="ok-text" /> : <CircleX size={15} className={job.status === 'failed' ? 'error-text' : 'faint'} />}
          <b>{job.status === 'succeeded' ? `${plural(job.deleted, 'version')} moved to the recycle bin` : job.status === 'cancelled' ? `Cancelled after ${plural(job.deleted, 'version')}` : 'Failed'}</b>
          <span className="faint">· {bytes(job.deletedBytes)}{job.finishedAt ? ` · finished ${relative(job.finishedAt)}` : ''}</span>
        </p>
      )}
      {job.skipped > 0 && <p className="faint" style={{ margin: '0 0 4px', fontSize: 12.5 }}>Skipped {plural(job.skipped, 'version')} in use: {REASONS(job.skippedReasons)}</p>}
      {job.error && <p className="error-text" style={{ margin: '0 0 4px' }}>{job.error}</p>}
      {!running && job.deleted > 0 && job.purgeAfter && (
        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>The space comes back on <span title={dateTime(job.purgeAfter)}>{day(job.purgeAfter)}</span> when the bin purges them, or <Link to="/recycle-bin" style={LINK}>empty the bin</Link> now.</p>
      )}
      {running && <button className="btn sm" style={{ marginTop: 8, alignSelf: 'flex-start' }} disabled={cancelling} onClick={onCancel} data-testid="old-versions-cancel"><Square size={12} /> Cancel</button>}
    </section>
  );
}
